import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Edit2, Trash2, RefreshCcw, Save, X, Search, Filter,
    CreditCard, DollarSign, Users, FileText, CheckCircle, AlertTriangle,
    Calendar, Building2, Eye, FolderSyncIcon
} from 'lucide-react';

const PaymentManagement = ({ currentOrganization = { organization_id: '1', name: 'Demo Organization' }, tokenStatus = 'connected' }) => {
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [alert, setAlert] = useState(null);

    // Pagination and search
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [searchTerm, setSearchTerm] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        invoice_no: '',
        customer_name: '',
        date_of_payment: '',
        amount: '',
        currency: 'INR',
        date_of_receipt: '',
        payment_mode: 'cash',
        transaction_reference_no: '',
        aws_link: '',
        bank_name: '',
        bank_account_last4: '',
        // Zoho-specific fields
        customer_id: '',
        invoice_id: '',
        account_id: '',
        autoSync: false
    });

    // Suggestions and dropdowns
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [stats, setStats] = useState({
        totalPayments: 0,
        totalAmount: 0,
        pendingSync: 0,
        syncedCount: 0
    });

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    // Show alert helper
    const showAlert = (message, type = 'info') => {
        setAlert({ message, type });
        setTimeout(() => setAlert(null), 5000);
    };

    // Get authentication headers
    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    // Safe fetch with error handling
    const safeFetch = async (url, options = {}) => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`Fetch error for ${url}:`, error);
            throw error;
        }
    };

    // Calculate stats from payments
    const calculateStats = (paymentsData) => {
        const totalPayments = paymentsData.length;
        const totalAmount = paymentsData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        const pendingSync = paymentsData.filter(p => p.sync_status === 'pending' || p.sync_status === 'error').length;
        const syncedCount = paymentsData.filter(p => p.sync_status === 'synced').length;

        setStats({ totalPayments, totalAmount, pendingSync, syncedCount });
    };

    // Fetch payments
    const fetchPayments = async () => {
        if (!currentOrganization?.organization_id) {
            return;
        }

        setIsLoading(true);
        try {
            const url = new URL(`${API_BASE_URL}/customer-payments`);
            url.searchParams.set('page', pagination.page);
            url.searchParams.set('limit', pagination.limit);
            url.searchParams.set('company_id', currentOrganization.organization_id);

            if (searchTerm) {
                url.searchParams.set('search', searchTerm);
            }

            const result = await safeFetch(url.toString(), {
                headers: getAuthHeaders()
            });

            if (result.success) {
                setPayments(result.data.payments);
                setPagination(prev => ({
                    ...prev,
                    total: result.data.total,
                    totalPages: result.data.totalPages
                }));
                calculateStats(result.data.payments);
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            showAlert('Error fetching payments: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Search customers in Zoho
    const fetchCustomerSuggestions = useCallback(async (searchName) => {
        if (!searchName || searchName.length < 2 || !currentOrganization?.organization_id) {
            setCustomerSuggestions([]);
            return;
        }

        try {
            const url = new URL(`${API_BASE_URL}/customer-payments/zoho/customers/search`);
            url.searchParams.set('name', searchName);
            url.searchParams.set('company_id', currentOrganization.organization_id);

            const result = await safeFetch(url.toString(), {
                headers: getAuthHeaders()
            });

            if (result.success) {
                setCustomerSuggestions(result.data);
            } else {
                setCustomerSuggestions([]);
            }
        } catch (error) {
            console.error('Error fetching customer suggestions:', error);
            setCustomerSuggestions([]);
        }
    }, [currentOrganization?.organization_id]);

    // Get customer invoices
    const fetchCustomerInvoices = useCallback(async (customerId) => {
        if (!customerId || !currentOrganization?.organization_id) {
            setCustomerInvoices([]);
            return;
        }

        try {
            const url = new URL(`${API_BASE_URL}/customer-payments/zoho/customers/invoices`);
            url.searchParams.set('customer_id', customerId);
            url.searchParams.set('company_id', currentOrganization.organization_id);

            const result = await safeFetch(url.toString(), {
                headers: getAuthHeaders()
            });

            if (result.success) {
                setCustomerInvoices(result.data);
            }
        } catch (error) {
            console.error('Error fetching customer invoices:', error);
            setCustomerInvoices([]);
        }
    }, [currentOrganization?.organization_id]);

    // Get payment accounts
    const fetchPaymentAccounts = useCallback(async () => {
        if (!currentOrganization?.organization_id) {
            return;
        }

        try {
            const url = new URL(`${API_BASE_URL}/customer-payments/zoho/accounts/payment`);
            url.searchParams.set('company_id', currentOrganization.organization_id);

            const result = await safeFetch(url.toString(), {
                headers: getAuthHeaders()
            });

            if (result.success) {
                setPaymentAccounts(result.data);
            }
        } catch (error) {
            console.error('Error fetching payment accounts:', error);
            setPaymentAccounts([]);
        }
    }, [currentOrganization?.organization_id]);

    // Handle customer name change
    const handleCustomerNameChange = async (value) => {
        setFormData(prev => ({ ...prev, customer_name: value }));

        if (value && value.length >= 2) {
            await fetchCustomerSuggestions(value);
            setShowCustomerSuggestions(true);
        } else {
            setShowCustomerSuggestions(false);
            setCustomerSuggestions([]);
        }
    };

    // Handle customer selection
    const handleCustomerSelect = async (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_name: customer.customer_name,
            customer_id: customer.customer_id,
            currency: customer.currency_code || 'INR'
        }));
        setShowCustomerSuggestions(false);
        setCustomerSuggestions([]);

        // Fetch customer invoices
        await fetchCustomerInvoices(customer.customer_id);
    };

    // Handle invoice selection
    const handleInvoiceSelect = (invoice) => {
        setFormData(prev => ({
            ...prev,
            invoice_no: invoice.invoice_number,
            invoice_id: invoice.invoice_id,
            amount: invoice.balance,
            currency: invoice.currency_code || prev.currency
        }));
    };

    // Initialize component
    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchPayments();
            fetchPaymentAccounts();
        }
    }, [pagination.page, searchTerm, currentOrganization?.organization_id]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.customer_name || !formData.invoice_no || !formData.date_of_payment || !formData.amount) {
            showAlert('Please fill in all required fields', 'error');
            return;
        }

        if (!currentOrganization?.organization_id) {
            showAlert('No organization selected', 'error');
            return;
        }

        setIsLoading(true);

        try {
            const formattedData = {
                ...formData,
                company_id: currentOrganization.organization_id,
                amount: parseFloat(formData.amount),
                date_of_payment: formData.date_of_payment,
                date_of_receipt: formData.date_of_receipt || formData.date_of_payment
            };

            const url = editingPayment
                ? `${API_BASE_URL}/customer-payments/${editingPayment.id}`
                : `${API_BASE_URL}/customer-payments`;

            const method = editingPayment ? 'PUT' : 'POST';

            const result = await safeFetch(url, {
                method,
                headers: getAuthHeaders(),
                body: JSON.stringify(formattedData)
            });

            if (result.success) {
                setShowForm(false);
                setEditingPayment(null);
                resetForm();
                fetchPayments();
                showAlert(editingPayment ? 'Payment updated successfully!' : 'Payment created successfully!', 'success');

                if (result.data.synced_to_zoho) {
                    showAlert('Payment also synced to Zoho Books!', 'success');
                }
            } else {
                showAlert('Error: ' + result.message, 'error');
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            showAlert('Error saving payment: ' + error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle edit
    const handleEdit = (payment) => {
        setEditingPayment(payment);

        const editFormData = {
            invoice_no: payment.invoice_no || '',
            customer_name: payment.customer_name || '',
            date_of_payment: payment.date_of_payment ? payment.date_of_payment.split('T')[0] : '',
            amount: payment.amount || '',
            currency: payment.currency || 'INR',
            date_of_receipt: payment.date_of_receipt ? payment.date_of_receipt.split('T')[0] : '',
            payment_mode: payment.payment_mode || 'cash',
            transaction_reference_no: payment.transaction_reference_no || '',
            aws_link: payment.aws_link || '',
            bank_name: payment.bank_name || '',
            bank_account_last4: payment.bank_account_last4 || '',
            customer_id: payment.customer_id || '',
            invoice_id: payment.invoice_id || '',
            account_id: payment.account_id || '',
            autoSync: false
        };

        setFormData(editFormData);
        setShowForm(true);
    };

    // Handle delete
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this payment?')) {
            try {
                const result = await safeFetch(`${API_BASE_URL}/customer-payments/${id}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });

                if (result.success) {
                    showAlert('Payment deleted successfully!', 'success');
                    fetchPayments();
                } else {
                    showAlert('Error: ' + result.message, 'error');
                }
            } catch (error) {
                console.error('Error deleting payment:', error);
                showAlert('Error deleting payment', 'error');
            }
        }
    };

    // Handle sync to Zoho
    const handleSyncToZoho = async (payment) => {
        // Check if already synced
        if (payment.sync_status === 'synced' && payment.zoho_payment_id) {
            showAlert('Payment is already synced to Zoho Books!', 'info');
            return;
        }

        // Check for required Zoho fields
        if (!payment.customer_id || !payment.invoice_id || !payment.account_id) {
            showAlert('Missing Zoho data. Please edit the payment and fill in Customer ID, Invoice ID, and Account ID.', 'warning');
            return;
        }

        try {
            showAlert('Syncing payment to Zoho Books...', 'info');

            const result = await safeFetch(`${API_BASE_URL}/customer-payments/${payment.id}/sync-zoho`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    customer_id: payment.customer_id,
                    invoice_id: payment.invoice_id,
                    account_id: payment.account_id,
                    company_id: currentOrganization.organization_id
                })
            });

            if (result.success) {
                showAlert('Payment synced to Zoho Books successfully!', 'success');
                fetchPayments(); // Refresh to show updated sync status
            } else {
                showAlert('Error syncing to Zoho: ' + result.message, 'error');
            }
        } catch (error) {
            console.error('Error syncing payment:', error);
            showAlert('Error syncing payment: ' + error.message, 'error');
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            invoice_no: '',
            customer_name: '',
            date_of_payment: '',
            amount: '',
            currency: 'INR',
            date_of_receipt: '',
            payment_mode: 'cash',
            transaction_reference_no: '',
            aws_link: '',
            bank_name: '',
            bank_account_last4: '',
            customer_id: '',
            invoice_id: '',
            account_id: '',
            autoSync: false
        });
        setCustomerSuggestions([]);
        setShowCustomerSuggestions(false);
        setCustomerInvoices([]);
    };

    // Format currency
    const formatCurrency = (amount, currency = 'INR') => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    // Get sync status badge
    const getSyncStatusBadge = (syncStatus) => {
        switch (syncStatus) {
            case 'synced':
                return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle size={12} className="mr-1" />
                    Synced
                </span>;
            case 'pending':
                return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <AlertTriangle size={12} className="mr-1" />
                    Pending
                </span>;
            case 'error':
                return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <X size={12} className="mr-1" />
                    Error
                </span>;
            case 'syncing':
                return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <RefreshCcw size={12} className="mr-1 animate-spin" />
                    Syncing
                </span>;
            default:
                return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Unknown
                </span>;
        }
    };

    if (!currentOrganization?.organization_id) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-red-600 w-6 h-6" />
                        <div>
                            <h3 className="text-red-800 font-semibold">Organization Not Found</h3>
                            <p className="text-red-700 mt-1">
                                Please select a Zoho Books organization to manage payments.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Alert */}
            {alert && (
                <div className={`mb-4 p-4 rounded-lg ${alert.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
                    alert.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
                        alert.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                            'bg-blue-100 text-blue-800 border border-blue-200'
                    } flex items-center gap-2`}>
                    {alert.type === 'error' && <X size={16} />}
                    {alert.type === 'success' && <CheckCircle size={16} />}
                    {alert.type === 'warning' && <AlertTriangle size={16} />}
                    {alert.type === 'info' && <FileText size={16} />}
                    <span>{alert.message}</span>
                    <button onClick={() => setAlert(null)} className="ml-auto">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <CreditCard className="w-8 h-8 text-blue-600" />
                        Receipt Management
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                    </p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={20} />
                    Add Payment
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <CreditCard className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Payments</p>
                            <p className="text-2xl font-semibold text-gray-900">{stats.totalPayments}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <DollarSign className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Amount</p>
                            <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Pending Sync</p>
                            <p className="text-2xl font-semibold text-gray-900">{stats.pendingSync}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <FolderSyncIcon className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Synced to Zoho</p>
                            <p className="text-2xl font-semibold text-gray-900">{stats.syncedCount}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="mb-6 flex gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search by customer name, invoice number, or reference..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
                <button
                    onClick={fetchPayments}
                    disabled={isLoading}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    <RefreshCcw size={16} className={isLoading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : payments.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No payments found</td>
                                </tr>
                            ) : (
                                payments.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Users className="w-4 h-4 text-gray-400 mr-2" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{payment.customer_name}</div>
                                                    {payment.bank_name && (
                                                        <div className="text-xs text-gray-500">{payment.bank_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <FileText className="w-4 h-4 text-gray-400 mr-2" />
                                                <span className="text-sm text-gray-900">{payment.invoice_no}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div className="flex items-center">
                                                <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                                {formatDate(payment.date_of_payment)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                                                <span className="text-sm font-medium text-green-600">
                                                    {formatCurrency(payment.amount, payment.currency)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {payment.payment_mode}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getSyncStatusBadge(payment.sync_status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {payment.transaction_reference_no || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(payment)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Edit"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                {payment.sync_status !== 'synced' && (
                                                    <button
                                                        onClick={() => handleSyncToZoho(payment)}
                                                        className="text-green-600 hover:text-green-900"
                                                        title="Sync to Zoho"
                                                    >
                                                        <RefreshCcw size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(payment.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-700">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded">
                        {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                                {editingPayment ? 'Edit Payment' : 'Add New Payment'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowForm(false);
                                    setEditingPayment(null);
                                    resetForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Customer Information */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    Customer Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Customer Name * (Search in Zoho)
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.customer_name}
                                            onChange={(e) => handleCustomerNameChange(e.target.value)}
                                            onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Start typing customer name..."
                                        />
                                        {/* Customer Suggestions Dropdown */}
                                        {showCustomerSuggestions && customerSuggestions.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {customerSuggestions.map((customer) => (
                                                    <div
                                                        key={customer.customer_id}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                                        onMouseDown={() => handleCustomerSelect(customer)}
                                                    >
                                                        <div className="font-medium text-gray-900">{customer.customer_name}</div>
                                                        <div className="text-gray-500 text-xs">
                                                            ID: {customer.customer_id}
                                                            {customer.outstanding_receivable_amount > 0 && (
                                                                <span className="ml-2 text-red-600">
                                                                    • Outstanding: {formatCurrency(customer.outstanding_receivable_amount, customer.currency_code)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Customer Zoho ID (Auto-filled)
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.customer_id}
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                                            placeholder="Will be auto-filled from Zoho Books"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Information */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-green-600" />
                                    Invoice Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Invoice Number *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.invoice_no}
                                            onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter invoice number"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Select Customer Invoice (Auto-fill)
                                        </label>
                                        <select
                                            value={formData.invoice_id}
                                            onChange={(e) => {
                                                const selectedInvoice = customerInvoices.find(inv => inv.invoice_id === e.target.value);
                                                if (selectedInvoice) {
                                                    handleInvoiceSelect(selectedInvoice);
                                                }
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            disabled={!formData.customer_id || customerInvoices.length === 0}
                                        >
                                            <option value="">
                                                {!formData.customer_id ? 'Select customer first' :
                                                    customerInvoices.length === 0 ? 'No unpaid invoices' :
                                                        'Select an invoice'}
                                            </option>
                                            {customerInvoices.map((invoice) => (
                                                <option key={invoice.invoice_id} value={invoice.invoice_id}>
                                                    {invoice.invoice_number} - {formatCurrency(invoice.balance, invoice.currency_code)}
                                                    ({invoice.status})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Details */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-purple-600" />
                                    Payment Details
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.date_of_payment}
                                            onChange={(e) => setFormData({ ...formData, date_of_payment: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter payment amount"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency
                                        </label>
                                        <select
                                            value={formData.currency}
                                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="INR">INR</option>
                                            <option value="USD">USD</option>
                                            <option value="EUR">EUR</option>
                                            <option value="GBP">GBP</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Mode
                                        </label>
                                        <select
                                            value={formData.payment_mode}
                                            onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="check">Check</option>
                                            <option value="creditcard">Credit Card</option>
                                            <option value="banktransfer">Bank Transfer</option>
                                            <option value="bankremittance">Bank Remittance</option>
                                            <option value="autobilling">Auto Billing</option>
                                            <option value="others">Others</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Receipt Date
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.date_of_receipt}
                                            onChange={(e) => setFormData({ ...formData, date_of_receipt: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Account (Zoho)
                                        </label>
                                        <select
                                            value={formData.account_id}
                                            onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">Select payment account</option>
                                            {paymentAccounts.map((account) => (
                                                <option key={account.account_id} value={account.account_id}>
                                                    {account.account_name} ({account.account_type})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Transaction Reference
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.transaction_reference_no}
                                            onChange={(e) => setFormData({ ...formData, transaction_reference_no: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter reference number"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.bank_name}
                                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter bank name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Account Last 4 Digits
                                        </label>
                                        <input
                                            type="text"
                                            maxLength="4"
                                            value={formData.bank_account_last4}
                                            onChange={(e) => setFormData({ ...formData, bank_account_last4: e.target.value.replace(/\D/g, '') })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Last 4 digits"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            AWS Link (Optional)
                                        </label>
                                        <input
                                            type="url"
                                            value={formData.aws_link}
                                            onChange={(e) => setFormData({ ...formData, aws_link: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>

                                {/* Auto Sync Option */}
                                <div className="mt-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.autoSync}
                                            onChange={(e) => setFormData({ ...formData, autoSync: e.target.checked })}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            Auto sync to Zoho Books (requires Customer ID, Invoice ID, and Account ID)
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="border-t pt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingPayment(null);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save size={16} />
                                    {isLoading ? 'Saving...' : editingPayment ? 'Update Payment' : 'Create Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentManagement;